import React, {
  useCallback,
  useState,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import AnnouncementContext from '../../../../../../context/AnnouncementContext';
import { getAnnouncementCommentCount, getCommunityLabel } from '../../../../../../utils/announcement';
import PodContext from '../../../../../../../context/PodContext';
import {
  InfoTag, LightTag, NoticeTag, ErrorTag,
} from '../../../../../../../components/Tags/styles';
import AttachmentSlider from '../../../../../../components/AttachmentSlider';
import LazyMountWrapper from '../../../../../../components/LazyMountWrapper';
import LinkifiedText from '../../../../../../../components/LinkifiedText';
import UserProfileCard from '../../../../../../components/UserProfileCard';
import AvatarSizeType from '../../../../../../../components/Avatar/AvatarSizes';
import { DateFormat, timeStampToStr } from '../../../../../../../utils/date';
import Announcement, {
  ANNOUNCEMENT_USER_TYPE_TAG_NAMES,
  SCHEDULED_STATUS,
  POST_MODERATION_CATEGORY_LABELS,
} from '../../../../../../Model/Announcement';
import ConfirmDialog from '../../../../../../components/ConfirmDialog';
import LikeButton from '../../../LikeButton';
import PostOptionsMenu from '../PostOptionsMenu';

import {
  Card,
  Message,
  TagContainer,
  BottomRow,
  IconContainer,
  CommentIcon,
  ScheduledIcon,
  FailedIcon,
  PostIconContainer,
  PinnedLabel,
  HeaderContainer,
  StyledPinIcon,
  ProfileCardWrapper,
  Banner,
  PartialCountsHint,
} from './styles';
import texts from './texts.json';

const AnnouncementItem = ({ announcement, isFullPostView, group }) => {
  const [isUnpinDialogOpen, setIsUnpinDialogOpen] = useState(false);

  const {
    updateCommentAndAnnouncementState,
    handleUnpinRequest,
    communities,
  } = useContext(AnnouncementContext);
  const { pods, isReady } = useContext(PodContext);

  const {
    message,
    tagName,
    communityName,
    createdAt,
    publishAt,
    scheduledStatus,
    likes,
    attachments,
    postAvatarUrl,
    postUserName,
    isByCoach,
    createdBy: clientId,
    coach: coachId,
    podId,
    isPinned,
    isHidden,
    hiddenCategory,
  } = announcement;

  const isScheduled = scheduledStatus === SCHEDULED_STATUS.PENDING;

  // A post sent to several communities exists as one doc per community. The card stands for the
  // whole group, so its counts are summed and its interactions are read-only - liking or unpinning
  // an aggregate has no single doc to act on. Those live in the per-community views instead.
  const isAggregated = (group?.siblings?.length || 0) > 1;
  const totalLikes = isAggregated ? group.totalLikes : likes;
  const totalComments = isAggregated ? group.totalComments : getAnnouncementCommentCount(announcement);

  const pinnedCommunityNames = useMemo(() => {
    if (!isAggregated) {
      return [];
    }
    return group.siblings
      .filter((sibling) => sibling.isPinned)
      .map((sibling) => getCommunityLabel(sibling.communityId, communities, sibling.communityName));
  }, [isAggregated, group, communities]);

  const hiddenSiblingCount = isAggregated
    ? group.siblings.filter((sibling) => sibling.isHidden).length
    : 0;

  // Community id -> the name its sibling stored, so a chip can still be labelled when the id no
  // longer resolves to one of the coach's communities (an archived one, which Community.getByCoach
  // filters out). Only covers ids whose sibling loaded; the rest fall back to the id.
  const siblingCommunityNames = useMemo(
    () => new Map((group?.siblings || []).map((sibling) => [sibling.communityId, sibling.communityName])),
    [group],
  );

  const confirmUnpin = useCallback(() => {
    handleUnpinRequest(announcement);
    setIsUnpinDialogOpen(false);
  }, [handleUnpinRequest, announcement]);

  const renderTag = useMemo(() => {
    if (isAggregated) {
      return (
        <TagContainer>
          {group.communityIds.map((communityId) => (
            <InfoTag key={communityId}>
              {getCommunityLabel(communityId, communities, siblingCommunityNames.get(communityId))}
            </InfoTag>
          ))}
        </TagContainer>
      );
    }

    if (communityName) {
      return (
        <TagContainer>
          <InfoTag>{communityName}</InfoTag>
        </TagContainer>
      );
    }

    const tagOption = Object.values(ANNOUNCEMENT_USER_TYPE_TAG_NAMES).find((option) => option.value === tagName);
    const podName = isReady && pods.find((pod) => pod.id === podId)?.name;

    return (
      <TagContainer>
        {tagOption ? (
          <LightTag>{tagOption.label}</LightTag>
        ) : (
          <InfoTag>{tagName}</InfoTag>
        )}
        {podName && <LightTag>{podName}</LightTag>}
      </TagContainer>
    );
  }, [isAggregated, group, communities, siblingCommunityNames, communityName, tagName, podId, pods, isReady]);

  // Pinning is per community, so an aggregate names where it is pinned rather than offering unpin.
  const renderPinnedTag = useMemo(() => {
    if (isAggregated) {
      if (!pinnedCommunityNames.length) {
        return null;
      }
      return (
        <NoticeTag>
          <StyledPinIcon />
          <PinnedLabel>
            {format(texts.pinnedIn, { communities: pinnedCommunityNames.join(', ') })}
          </PinnedLabel>
        </NoticeTag>
      );
    }
    if (!isPinned) {
      return null;
    }
    return (
      <NoticeTag onClick={() => setIsUnpinDialogOpen(true)}>
        <StyledPinIcon />
        <PinnedLabel>{texts.pinned}</PinnedLabel>
      </NoticeTag>
    );
  }, [isAggregated, pinnedCommunityNames, isPinned]);

  // Moderation is per community too, so an aggregate reports how many copies are hidden.
  const renderHiddenTag = useMemo(() => {
    if (isAggregated) {
      if (!hiddenSiblingCount) {
        return null;
      }
      return (
        <ErrorTag>
          {format(texts.hiddenInCommunities, {
            count: hiddenSiblingCount,
            total: group.communityIds.length,
          })}
        </ErrorTag>
      );
    }
    if (!isHidden) {
      return null;
    }
    return (
      <ErrorTag>
        {format(texts.hidden, {
          category: POST_MODERATION_CATEGORY_LABELS[hiddenCategory] || texts.hiddenUnknownCategory,
        })}
      </ErrorTag>
    );
  }, [isAggregated, group, hiddenSiblingCount, isHidden, hiddenCategory]);

  return (
    <>
      <Card $isFullPostView={isFullPostView} $isPinned={isAggregated ? !!pinnedCommunityNames.length : isPinned}>
        {isScheduled && publishAt && moment(publishAt.toDate()).isAfter(moment()) && (
          <Banner>
            <ScheduledIcon />
            {format(texts.scheduledFor, {
              date: timeStampToStr(publishAt, DateFormat.DATE_MONTH_TIME_SHORT_DISPLAY_FORMAT),
            })}
          </Banner>
        )}
        {isScheduled && publishAt && moment(publishAt.toDate()).isBefore(moment()) && (
          <Banner $isError>
            <FailedIcon />
            {texts.publishFailed}
          </Banner>
        )}
        <HeaderContainer>
          <ProfileCardWrapper>
            <UserProfileCard
              clientId={isByCoach ? coachId : clientId}
              avatarSize={AvatarSizeType.MEDIUM}
              dataRow={{
                userAvatar: postAvatarUrl,
                userName: postUserName,
              }}
              subTitle={moment(createdAt.toDate()).fromNow()}
              hideActions
            />
          </ProfileCardWrapper>
          {renderPinnedTag}
          {renderHiddenTag}
        </HeaderContainer>
        <Message><LinkifiedText text={message} /></Message>
        {renderTag}
        {attachments.length > 0 && (
          // In the full post view the slider is always visible, so lazy mounting
          // only adds an async placeholder flash with no benefit. Lazy mount in the
          // list view only, where most posts are off-screen on load.
          isFullPostView ? (
            <AttachmentSlider attachments={attachments} maxHeight="175px" />
          ) : (
            <LazyMountWrapper>
              <AttachmentSlider attachments={attachments} />
            </LazyMountWrapper>
          )
        )}
        <BottomRow $isScheduled={isScheduled}>
          {!isScheduled && (
            <PostIconContainer>
              <LikeButton
                announcementId={announcement.id}
                count={totalLikes}
                isReadOnly={isAggregated}
              />
              <IconContainer
                {...(!isFullPostView && { onClick: () => updateCommentAndAnnouncementState(announcement) })}
                $isFullPostView={isFullPostView}
              >
                <CommentIcon />
                {`${totalComments} ${texts.comments}`}
              </IconContainer>
              {isAggregated && !!group.missingSiblingCount && (
                <PartialCountsHint>{texts.partialCounts}</PartialCountsHint>
              )}
            </PostIconContainer>
          )}
          <PostOptionsMenu
            announcement={announcement}
            onUnpinClick={() => setIsUnpinDialogOpen(true)}
            coachOnly={!!isByCoach}
            isAggregated={isAggregated}
          />
        </BottomRow>
      </Card>
      <ConfirmDialog
        isOpen={isUnpinDialogOpen}
        onCancel={() => setIsUnpinDialogOpen(false)}
        onConfirm={confirmUnpin}
        dialogTexts={{
          title: texts.unpinAnnouncement,
          content: texts.unpinAnnouncementMessage,
          confirm: texts.unpinConfirm,
        }}
      />
    </>
  );
};

AnnouncementItem.propTypes = {
  announcement: PropTypes.instanceOf(Announcement).isRequired,
  isFullPostView: PropTypes.bool,
  // The post's group, when the card stands for a multi-community post.
  group: PropTypes.shape({
    siblings: PropTypes.arrayOf(PropTypes.instanceOf(Announcement)),
    communityIds: PropTypes.arrayOf(PropTypes.string),
    totalLikes: PropTypes.number,
    totalComments: PropTypes.number,
    missingSiblingCount: PropTypes.number,
  }),
};

AnnouncementItem.defaultProps = {
  isFullPostView: false,
  group: null,
};

export default compose(
  observer,
)(AnnouncementItem);
